/**
 * Mermaid class diagram parser.
 * Falls back to regex parsing if mermaid.js API is unavailable.
 */

import type { IRGraph, IRNode, IREdge, Direction } from '../types';
import { NodeShape, createEmptyGraph } from '../types';
import { SHAPE_REGISTRY } from '../registry';

export interface ClassMember {
  visibility: string; // +, -, #, ~
  name: string;
  type: string;
}

export interface ClassMethod {
  visibility: string;
  name: string;
  returnType: string;
}

let mermaidInstance: typeof import('mermaid')['default'] | null = null;

async function getMermaid() {
  if (!mermaidInstance) {
    const mod = await import('mermaid');
    mermaidInstance = mod.default;
    mermaidInstance.initialize({ startOnLoad: false, logLevel: 5 as any });
  }
  return mermaidInstance;
}

export async function parseMermaidClass(text: string): Promise<IRGraph> {
  try {
    return await parseMermaidApi(text);
  } catch {
    return parseRegex(text);
  }
}

async function parseMermaidApi(text: string): Promise<IRGraph> {
  const mermaid = await getMermaid();
  const diagram = await mermaid.mermaidAPI.getDiagramFromText(text);
  const db = (diagram as any).db;

  const graph = createEmptyGraph('TD', 'class');

  const classes: Map<string, any> = db.getClasses?.() ?? new Map();
  const relations: any[] = db.getRelations?.() ?? [];

  for (const [id, cls] of classes) {
    const members: ClassMember[] = [];
    const methods: ClassMethod[] = [];

    for (const m of cls.members ?? []) {
      const text = typeof m === 'string' ? m : m.text ?? '';
      if (text.includes('(')) {
        methods.push(parseMethodString(text));
      } else {
        members.push(parseMemberString(text));
      }
    }

    // Compute dynamic dimensions
    const longestLine = Math.max(
      id.length,
      ...members.map((m) => `${m.visibility}${m.type} ${m.name}`.length),
      ...methods.map((m) => `${m.visibility}${m.name}() ${m.returnType}`.length),
    );
    const width = Math.max(SHAPE_REGISTRY[NodeShape.ClassBox].defaultWidth, longestLine * 8 + 20);
    const height = 30 + (members.length + methods.length) * 20 + 20;

    const node: IRNode = {
      id,
      label: cls.id ?? id,
      shape: NodeShape.ClassBox,
      width,
      height,
      meta: {
        members,
        methods,
        annotations: cls.annotations ?? [],
      },
    };
    graph.nodes.set(id, node);
  }

  // If API returned no classes, fall back
  if (graph.nodes.size === 0) {
    return parseRegex(text);
  }

  let edgeCounter = 0;
  for (const rel of relations) {
    edgeCounter++;
    const relType = classifyRelationType(rel);
    const edge: IREdge = {
      id: `e${edgeCounter}`,
      source: rel.id1,
      target: rel.id2,
      label: rel.relationTitle1 || rel.title || undefined,
      dashed: relType === 'dependency',
      meta: { relationType: relType },
    };
    graph.edges.push(edge);
  }

  return graph;
}

function classifyRelationType(rel: any): string {
  // mermaid relation types
  const type1 = rel.relation?.type1;
  const type2 = rel.relation?.type2;
  const lineType = rel.relation?.lineType;

  if (type2 === 'extension' || type1 === 'extension') return 'inheritance';
  if (type2 === 'composition' || type1 === 'composition') return 'composition';
  if (type2 === 'aggregation' || type1 === 'aggregation') return 'aggregation';
  if (lineType === 1) return 'dependency'; // dotted line
  return 'association';
}

// ─── Regex fallback ──────────────────────────────────────────────

const CLASS_BLOCK_RE = /class\s+(\w+)\s*(?:\{([^}]*)\})?/gs;
const MEMBER_RE = /^\s*([+\-#~]?)(\w[\w<>~]*)\s+(\w+)\s*$/;
const METHOD_RE = /^\s*([+\-#~]?)(\w+)\(([^)]*)\)\s*(?::\s*)?(\w*)\s*$/;
const RELATION_RE =
  /(\w+)\s+(<\|--|--\*|--o|o--|<\.\.|\.\.|-->|--)\s+(\w+)\s*(?::\s*(.+))?/g;
const DIRECTION_RE = /direction\s+(TB|TD|LR|RL|BT)/;

function parseRegex(text: string): IRGraph {
  const graph = createEmptyGraph('TD', 'class');

  // Direction
  const dirMatch = DIRECTION_RE.exec(text);
  if (dirMatch) graph.direction = dirMatch[1] as Direction;

  // Parse class blocks
  CLASS_BLOCK_RE.lastIndex = 0;
  let cm: RegExpExecArray | null;
  while ((cm = CLASS_BLOCK_RE.exec(text)) !== null) {
    const className = cm[1];
    const body = cm[2] ?? '';
    const members: ClassMember[] = [];
    const methods: ClassMethod[] = [];

    for (const line of body.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      const methodMatch = METHOD_RE.exec(trimmed);
      if (methodMatch) {
        methods.push({
          visibility: methodMatch[1] || '+',
          name: methodMatch[2],
          returnType: methodMatch[4] || 'void',
        });
        continue;
      }

      const memberMatch = MEMBER_RE.exec(trimmed);
      if (memberMatch) {
        members.push({
          visibility: memberMatch[1] || '+',
          name: memberMatch[3],
          type: memberMatch[2],
        });
      }
    }

    const longestLine = Math.max(
      className.length,
      ...members.map((m) => `${m.visibility}${m.type} ${m.name}`.length),
      ...methods.map((m) => `${m.visibility}${m.name}() ${m.returnType}`.length),
      1,
    );
    const width = Math.max(SHAPE_REGISTRY[NodeShape.ClassBox].defaultWidth, longestLine * 8 + 20);
    const height = 30 + (members.length + methods.length) * 20 + 20;

    const node: IRNode = {
      id: className,
      label: className,
      shape: NodeShape.ClassBox,
      width,
      height,
      meta: { members, methods, annotations: [] },
    };
    graph.nodes.set(className, node);
  }

  // Parse relations
  RELATION_RE.lastIndex = 0;
  let rm: RegExpExecArray | null;
  while ((rm = RELATION_RE.exec(text)) !== null) {
    const src = rm[1];
    const arrow = rm[2];
    const tgt = rm[3];
    const label = rm[4]?.trim() || undefined;

    // Ensure nodes exist (may be referenced but not defined with body)
    ensureClassNode(graph, src);
    ensureClassNode(graph, tgt);

    const relationType = classifyArrow(arrow);
    graph.edges.push({
      id: `e${graph.edges.length + 1}`,
      source: src,
      target: tgt,
      label,
      dashed: relationType === 'dependency',
      meta: { relationType },
    });
  }

  return graph;
}

function ensureClassNode(graph: IRGraph, id: string): void {
  if (graph.nodes.has(id)) return;
  const entry = SHAPE_REGISTRY[NodeShape.ClassBox];
  graph.nodes.set(id, {
    id,
    label: id,
    shape: NodeShape.ClassBox,
    width: entry.defaultWidth,
    height: entry.defaultHeight,
    meta: { members: [], methods: [], annotations: [] },
  });
}

function classifyArrow(arrow: string): string {
  if (arrow === '<|--' || arrow === '--|>') return 'inheritance';
  if (arrow === '--*') return 'composition';
  if (arrow === '--o' || arrow === 'o--') return 'aggregation';
  if (arrow === '<..' || arrow === '..') return 'dependency';
  return 'association';
}

function parseMemberString(text: string): ClassMember {
  const vis = /^[+\-#~]/.test(text) ? text[0] : '+';
  const rest = /^[+\-#~]/.test(text) ? text.slice(1).trim() : text.trim();
  const parts = rest.split(/\s+/);
  return {
    visibility: vis,
    type: parts[0] ?? 'unknown',
    name: parts[1] ?? parts[0] ?? 'unknown',
  };
}

function parseMethodString(text: string): ClassMethod {
  const vis = /^[+\-#~]/.test(text) ? text[0] : '+';
  const rest = /^[+\-#~]/.test(text) ? text.slice(1).trim() : text.trim();
  const match = /(\w+)\(([^)]*)\)\s*(?::\s*)?(\w*)/.exec(rest);
  if (match) {
    return { visibility: vis, name: match[1], returnType: match[3] || 'void' };
  }
  return { visibility: vis, name: rest, returnType: 'void' };
}
